import { CurationItem } from "../../types";
import { DEFAULT_SMART_LIST_ADAPTER_ID, DEFAULT_SMART_LIST_SORT_OPTION } from "../../constants";
import { callCurationProxy } from "../backend/backendClient";
import {
    DEFAULT_SMART_LIST_TEMPLATE_ID,
    ensureTrimmed,
    listLogger,
    parseJsonOrThrow,
    readTextOrThrow,
    resolvePublisherId,
    resolveSmartListLabels,
} from "./helpers";

/**
 * Options for creating a list
 * @property {string} [description] - Description of the list
 * @property {string} [companyId] - Company ID to use for the list
 * @property {string} [publisherId] - Publisher ID to use for the list
 * @property {string} [themeBrandColor] - Theme brand color to use for the list
 * @property {boolean} [isSmartList] - Whether the list is a smart list
 * @property {string} [smartListTemplate] - Smart list template ID to use for the list
 * @property {number} [smartListAdapterId] - Smart list adapter ID to use for the list
 * @property {string} [smartListGenre] - Smart list genre to use for the list
 * @property {string[]} [smartListLabels] - Smart list labels to use for the list
 * @property {string} [smartListSortOption] - Smart list sort option to use for the list
 */
export interface CreateListOptions {
    description?: string;
    companyId?: string;
    publisherId?: string;
    themeBrandColor?: string;
    isSmartList?: boolean;
    smartListTemplate?: string;
    smartListAdapterId?: number;
    smartListGenre?: string;
    smartListLabels?: string[];
    smartListSortOption?: string;
}

/**
 * Create a list
 * @param {string} parentId - The ID of the parent container
 * @param {string} contentType - The type of content (e.g., 'list', 'smart_list', 'carousel')
 * @param {string} title - The title of the list
 * @param {string} [alias=''] - Optional alias for the list
 * @param {CreateListOptions} [options={}] - Optional configuration for the list
 * @returns {Promise<CurationItem>} The created list item
 */
export const createList = async (
    parentId: string,
    contentType: string,
    title: string,
    alias: string = "",
    options: CreateListOptions = {},
): Promise<CurationItem> => {
    if (!parentId || !contentType || !title) {
        throw new Error("createList requires a parentId, contentType, and title.");
    }

    const trimmedAlias = alias.trim();
    const {
        description,
        companyId,
        publisherId,
        themeBrandColor,
        isSmartList,
        smartListTemplate,
        smartListAdapterId,
        smartListGenre,
        smartListLabels,
        smartListSortOption,
    } = options;
    const normalizedPublisherId = resolvePublisherId(publisherId);
    const normalizedThemeBrandColor = ensureTrimmed(themeBrandColor);

    const normalizedDescription = ensureTrimmed(description);
    const normalizedContentType = contentType?.toLowerCase?.() ?? "";
    const smartListEnabled = [
        Boolean(isSmartList),
        normalizedContentType === "smart_list",
        normalizedContentType === "list_smart",
    ].some(Boolean);

    let actualContentType: string;
    if (smartListEnabled) {
        actualContentType = "list_smart";
    } else if (normalizedContentType === "carousel") {
        actualContentType = "carousel";
    } else {
        actualContentType = "list";
    }

    const resolvedLabels = resolveSmartListLabels(smartListLabels, smartListGenre);
    const resolvedSortOption = ensureTrimmed(smartListSortOption) ?? DEFAULT_SMART_LIST_SORT_OPTION;
    const templateIdFromOptions = Number((smartListTemplate ?? "").trim());
    const resolvedTemplateId =
        Number.isFinite(templateIdFromOptions) && templateIdFromOptions > 0
            ? templateIdFromOptions
            : undefined;

    const adapterIdFromOptions = Number(smartListAdapterId);
    const resolvedAdapterId =
        Number.isFinite(adapterIdFromOptions) && adapterIdFromOptions > 0
            ? adapterIdFromOptions
            : undefined;

    const buildSmartListPayload = (existing?: CurationItem) => {
        const existingRequestMap =
            (
                existing as CurationItem & {
                    requestMap?: { options?: Record<string, unknown> };
                }
            )?.requestMap ?? {};
        const existingOptions = existingRequestMap.options ?? {};
        return {
            ...existing,
            values: {
                ...(existing?.values ?? {}),
                sort: resolvedSortOption,
                labels: resolvedLabels,
            },
            requestMap: {
                ...existingRequestMap,
                url: "https://sales.content-discovery.cf.eu-north-1-prod.vmnd.tv/api/v1/assets",
                method: "get",
                options: {
                    ...existingOptions,
                    json: true,
                    query: {
                        page: {
                            size: "50",
                        },
                        sort: resolvedSortOption,
                        query: `labels:${resolvedLabels}`,
                        platform: "web",
                    },
                    headers: {
                        Accept: "application/json",
                        "Accept-Language": "en-us",
                        "Content-Type": "application/json",
                    },
                    timeout: 2000,
                },
                firstSortIndex: 0,
            },
        };
    };

    const createBody = [
        {
            contentType: actualContentType,
            parentId,
            metadata: {
                default: {
                    "publisher-id": normalizedPublisherId,
                    ...(normalizedDescription
                        ? { "description-short": normalizedDescription }
                        : {}),
                },
                en_US: {},
            },
            ...(smartListEnabled && {
                templateId: resolvedTemplateId ?? DEFAULT_SMART_LIST_TEMPLATE_ID,
                adapterId: resolvedAdapterId ?? DEFAULT_SMART_LIST_ADAPTER_ID,
                smartList: buildSmartListPayload(),
            }),
        },
    ];

    const createResponseText = await readTextOrThrow(
        await callCurationProxy({
            path: "/list/?index=0&copyImagePack=true",
            method: "POST",
            body: createBody,
        }),
        "List creation (POST)",
        (status, text) => `POST Error: ${status}, ${text}`,
    );

    const creationData = parseJsonOrThrow<{ result?: CurationItem[] }>(
        createResponseText,
        "List creation",
    );
    const newList = creationData.result?.[0];

    if (!newList?.id || !newList?.listId) {
        throw new Error(
            `API Error: List creation succeeded but critical IDs were not returned. Response: ${createResponseText}`,
        );
    }

    listLogger.debug("POST response newList:", JSON.stringify(newList, null, 2));

    const updateBody = {
        ...newList,
        title,
        ...(smartListEnabled && {
            templateId: resolvedTemplateId ?? DEFAULT_SMART_LIST_TEMPLATE_ID,
            adapterId: resolvedAdapterId ?? newList.adapterId ?? DEFAULT_SMART_LIST_ADAPTER_ID,
            smartList: buildSmartListPayload(newList.smartList),
        }),
        metadata: {
            ...newList.metadata,
            default: {
                ...newList.metadata.default,
                ...(trimmedAlias ? { alias: trimmedAlias } : {}),
                ...(normalizedDescription ? { "description-short": normalizedDescription } : {}),
                ...(companyId ? { "company-id": companyId } : {}),
                ...(normalizedPublisherId ? { "publisher-id": normalizedPublisherId } : {}),
                ...(normalizedThemeBrandColor
                    ? { "theme-brand-color": normalizedThemeBrandColor }
                    : {}),
            },
            en_US: {
                ...newList.metadata.en_US,
                title,
            },
        },
    };

    listLogger.debug("PUT updateBody:", JSON.stringify(updateBody, null, 2));

    await readTextOrThrow(
        await callCurationProxy({
            path: `/list/${newList.id}`,
            method: "PUT",
            body: updateBody,
        }),
        "List update (PUT)",
        (status, text) => `PUT Error: ${status}, ${text}`,
    );

    return newList;
};
